import React, { useState } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function HunterJourneyLadder() {
  const [activePhase, setActivePhase] = useState(null);
  const [expandedPhase, setExpandedPhase] = useState(null);

  const phases = [
    {
      id: 1,
      title: "Education & Legal Foundation",
      subtitle: "Start at the Ground Level",
      image: `${BACKEND_URL}/images/hunter_journey/Hunter_at_ladder_base_c291c1e0.png`,
      position: "Ground Level",
      description: "Begin your journey with hunter education certification and understanding regulations",
      color: "blue",
      content: {
        title: "Education & Legal Foundation",
        sections: [
          {
            title: "Hunter Education Course",
            items: [
              "Register online at your state wildlife agency website",
              "Choose online ($20-50) or in-person (often free) format", 
              "Study topics: safety, ethics, wildlife ID, regulations, first aid",
              "Pass exam with 80% or higher (multiple attempts allowed)",
              "Field day required in most states (hands-on practice)",
              "Certificate valid for life in all 50 states"
            ]
          },
          {
            title: "Licenses & Permits",
            items: [
              "Base hunting license ($15-50 residents, $100-350 non-residents)",
              "Species tags: Deer ($10-30), Turkey ($15-25), Small game ($10-20)",
              "Federal duck stamp if waterfowl hunting ($25)",
              "Special permits: Archery endorsement, muzzleloader stamp",
              "Consider combo packages for better value",
              "Youth/senior discounts available (often 50% off)"
            ]
          }
        ]
      }
    },
    {
      id: 2,
      title: "Skills Development",
      subtitle: "First Steps Up",
      image: `${BACKEND_URL}/images/hunter_journey/Hunter_climbing_first_steps_cc5d404a.png`,
      position: "25% Up",
      description: "Develop marksmanship, field skills, and practice at the range",
      color: "purple",
      content: {
        title: "Skills Development",
        sections: [
          {
            title: "Marksmanship",
            items: [
              "Join local range ($200-500/year)",
              "Take basic rifle/bow course",
              "Practice weekly (50+ rounds/arrows)",
              "Master 3 positions: prone, kneeling, standing",
              "Achieve 4\" groups at 100 yards",
              "Know your effective range limits"
            ]
          },
          {
            title: "Field Skills",
            items: [
              "Master map & compass navigation",
              "Learn GPS/hunting apps (onX, HuntStand)",
              "Practice judging distances",
              "Study animal tracks & sign",
              "Learn wind reading & scent control"
            ]
          },
          {
            title: "Processing Skills",
            items: [
              "Watch field dressing videos",
              "Practice on small game first",
              "Learn proper meat handling",
              "Find local processor as backup",
              "Invest in chest freezer ($200-400)"
            ]
          }
        ]
      }
    },
    {
      id: 3,
      title: "Gear Acquisition",
      subtitle: "Halfway to Success",
      image: `${BACKEND_URL}/images/hunter_journey/Hunter_midway_up_ladder_8d703379.png`,
      position: "50% Up",
      description: "Invest in quality equipment and build your hunting kit",
      color: "orange",
      content: {
        title: "Smart Gear Acquisition",
        sections: [
          {
            title: "Budget Start ($500-800)",
            items: [
              "Used rifle/shotgun ($200-400)",
              "Basic scope or iron sights ($50-150)",
              "Military surplus camo ($50-100)",
              "Orange vest & hat ($20)",
              "Basic hunting knife ($30-50)",
              "Day pack from discount store ($30)"
            ]
          },
          {
            title: "Recommended Setup ($1,500-2,500)",
            items: [
              "Quality rifle/bow ($500-800)",
              "Good optics ($200-400)",
              "Proper clothing system ($300-500)",
              "Quality boots ($150-250)",
              "Rangefinder ($100-200)",
              "Complete field kit ($100)"
            ]
          }
        ],
        tip: "💡 Buy quality optics and boots first - you'll spend more time looking and walking than shooting."
      }
    },
    {
      id: 4,
      title: "Find Opportunities",
      subtitle: "Nearly There",
      image: `${BACKEND_URL}/images/hunter_journey/Hunter_near_tree_stand_top_ddc88eb9.png`,
      position: "75% Up",
      description: "Scout locations, secure permissions, and plan your hunts",
      color: "green",
      content: {
        title: "Finding Hunting Opportunities",
        sections: [
          {
            title: "Public Land Options",
            items: [
              "State Wildlife Management Areas (free)",
              "National Forests (millions of acres)",
              "State Game Lands (managed for hunting)",
              "Walk-In Access programs",
              "Army Corps of Engineers land",
              "Bureau of Land Management (Western)"
            ]
          },
          {
            title: "Private Land Access",
            items: [
              "Door knocking - ask farmers politely",
              "Offer farm work in exchange",
              "Hunting leases ($500-2000/year)",
              "Join hunting club ($300-1000/year)",
              "Network at sporting goods stores",
              "Guided hunts for first time ($500-1500)"
            ]
          }
        ]
      }
    },
    {
      id: 5,
      title: "First Hunt",
      subtitle: "At the Top - Ready",
      image: `${BACKEND_URL}/images/hunter_journey/Hunter_settled_at_top_8522242e.png`,
      position: "Summit",
      description: "Execute your first hunt with confidence and safety",
      color: "red",
      content: {
        title: "Your First Hunt",
        sections: [
          {
            title: "Pre-Season Scouting",
            items: [
              "Scout area 2-4 weeks before season",
              "Identify trails, feeding & bedding areas",
              "Set trail cameras if allowed",
              "Mark stand locations with GPS",
              "Check sunrise/sunset & wind patterns",
              "Have backup spots ready"
            ]
          },
          {
            title: "Hunt Day Execution",
            items: [
              "Arrive 1 hour before legal time",
              "Move slowly and quietly",
              "Stay patient (4-6 hour sits)",
              "Only take ethical shots",
              "Wait 30 min after shot",
              "Have processor lined up"
            ]
          }
        ],
        success: "Success isn't just harvest - it's seeing wildlife, staying safe, gaining confidence, and enjoying nature."
      }
    }
  ];

  const handlePhaseClick = (phaseId) => {
    if (expandedPhase) {
      // If already expanded, clicking anywhere resets
      setExpandedPhase(null);
      setActivePhase(null);
    } else {
      // If not expanded, expand the clicked phase
      setExpandedPhase(phaseId);
      setActivePhase(phaseId);
    }
  };

  const selectedPhaseData = expandedPhase ? phases.find(p => p.id === expandedPhase) : null;

  return (
    <div className="bg-gray-900 rounded-lg p-6 mt-6">
      <div className="text-center mb-8">
        <h3 className="text-3xl font-bold text-white mb-3">
          🪜 Your Climbing Journey to Hunting Success
        </h3>
        <p className="text-gray-300 max-w-2xl mx-auto">
          Just like climbing a tree stand, becoming a hunter is a step-by-step journey. 
          The ladder represents <span className="text-emerald-400 font-semibold">mentorship</span> - 
          supporting you at every step of your ascent.
        </p>
      </div>

      {/* Expanded View */}
      {expandedPhase && selectedPhaseData ? (
        <div 
          className="relative bg-gray-800/50 rounded-lg p-6 cursor-pointer min-h-[500px]" 
          onClick={() => handlePhaseClick(null)}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 h-full">
            {/* Left Side - Large Image */}
            <div className="flex items-center justify-center">
              <img
                src={selectedPhaseData.image}
                alt={selectedPhaseData.title}
                className="w-full h-auto max-h-[450px] object-contain rounded-lg shadow-2xl"
              />
            </div>

            {/* Right Side - Content on Transparent Background */}
            <div className="bg-black/40 backdrop-blur-sm rounded-lg p-4 overflow-y-auto max-h-[450px]">
              <h3 className={`
                text-xl font-bold mb-3
                ${selectedPhaseData.color === 'blue' ? 'text-blue-400' : ''}
                ${selectedPhaseData.color === 'purple' ? 'text-purple-400' : ''}
                ${selectedPhaseData.color === 'orange' ? 'text-orange-400' : ''}
                ${selectedPhaseData.color === 'green' ? 'text-green-400' : ''}
                ${selectedPhaseData.color === 'red' ? 'text-red-400' : ''}
              `}>
                {selectedPhaseData.content.title}
              </h3>
              
              <div className="space-y-2">
                {selectedPhaseData.content.sections.map((section, idx) => (
                  <div key={idx}>
                    <h4 className={`
                      font-semibold text-base mb-1
                      ${selectedPhaseData.color === 'blue' ? 'text-blue-300' : ''}
                      ${selectedPhaseData.color === 'purple' ? 'text-purple-300' : ''}
                      ${selectedPhaseData.color === 'orange' ? 'text-orange-300' : ''}
                      ${selectedPhaseData.color === 'green' ? 'text-green-300' : ''}
                      ${selectedPhaseData.color === 'red' ? 'text-red-300' : ''}
                    `}>
                      {section.title}
                    </h4>
                    <ul className="space-y-0.5">
                      {section.items.map((item, i) => (
                        <li key={i} className="text-sm text-gray-200 flex gap-1">
                          <span className={`mt-0.5 flex-shrink-0
                            ${selectedPhaseData.color === 'blue' ? 'text-blue-400' : ''}
                            ${selectedPhaseData.color === 'purple' ? 'text-purple-400' : ''}
                            ${selectedPhaseData.color === 'orange' ? 'text-orange-400' : ''}
                            ${selectedPhaseData.color === 'green' ? 'text-green-400' : ''}
                            ${selectedPhaseData.color === 'red' ? 'text-red-400' : ''}
                          `}>•</span>
                          <span className="leading-snug">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              
              {selectedPhaseData.content.tip && (
                <div className="mt-2 p-2 bg-yellow-500/20 border border-yellow-400/50 rounded text-sm text-yellow-200">
                  {selectedPhaseData.content.tip}
                </div>
              )}
              
              {selectedPhaseData.content.success && (
                <div className="mt-2 p-2 bg-green-500/20 border border-green-400/50 rounded text-sm text-green-200">
                  {selectedPhaseData.content.success}
                </div>
              )}
            </div>
          </div>

          {/* Click to Reset Instruction */}
          <div className="absolute bottom-2 right-2 text-gray-400 text-xs">
            Click anywhere to return to all phases
          </div>
        </div>
      ) : (
        /* Normal Grid View */
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {phases.map((phase) => (
            <div
              key={phase.id}
              className={`
                relative group cursor-pointer transition-all duration-300
                ${activePhase === phase.id ? 'scale-105 z-10' : 'hover:scale-102'}
              `}
              onClick={() => handlePhaseClick(phase.id)}
            >
            {/* Image Container */}
            <div className="relative overflow-hidden rounded-lg">
              <img
                src={phase.image}
                alt={`${phase.title} - ${phase.position}`}
                className={`
                  w-full h-64 object-cover transition-all duration-300
                  ${activePhase === phase.id ? 'brightness-100' : 'brightness-75 group-hover:brightness-90'}
                `}
              />
              

              {/* Position Badge */}
              <div className="absolute top-3 right-3 bg-black/60 text-white px-2 py-1 rounded text-xs font-semibold">
                {phase.position}
              </div>

              {/* Title Overlay */}
              <div className={`
                absolute bottom-0 left-0 right-0 p-4
                bg-gradient-to-t from-black/90 to-transparent
              `}>
                <p className="text-white font-bold text-sm">
                  {phase.title}
                </p>
              </div>
            </div>

            {/* Speech Bubble with Detailed Content - Remove for expanded view */}
            {!expandedPhase && activePhase === phase.id && phase.content && (
              <div className={`
                absolute z-30 bg-white rounded-lg shadow-2xl border-2
                ${phase.id <= 3 ? 'top-full mt-4' : 'bottom-full mb-4'}
                ${phase.id === 1 || phase.id === 5 ? 'left-0' : ''}
                ${phase.id === 2 || phase.id === 4 ? 'left-1/2 -translate-x-1/2' : ''}
                ${phase.id === 3 ? 'right-0' : ''}
                ${phase.color === 'blue' ? 'border-blue-500' : ''}
                ${phase.color === 'purple' ? 'border-purple-500' : ''}
                ${phase.color === 'orange' ? 'border-orange-500' : ''}
                ${phase.color === 'green' ? 'border-green-500' : ''}
                ${phase.color === 'red' ? 'border-red-500' : ''}
              `}
              style={{ minWidth: '400px', maxWidth: '500px' }}
              >
                {/* Speech Bubble Tail */}
                <div className={`
                  absolute w-0 h-0 
                  ${phase.id <= 3 ? '-top-2' : '-bottom-2'}
                  ${phase.id === 1 || phase.id === 5 ? 'left-8' : ''}
                  ${phase.id === 2 || phase.id === 4 ? 'left-1/2 -translate-x-1/2' : ''}
                  ${phase.id === 3 ? 'right-8' : ''}
                  border-l-[8px] border-l-transparent
                  border-r-[8px] border-r-transparent
                  ${phase.id <= 3 ? 'border-b-[8px] border-b-white' : 'border-t-[8px] border-t-white'}
                `}></div>
                
                {/* Content */}
                <div className="p-5">
                  <h3 className={`
                    text-lg font-bold mb-3
                    ${phase.color === 'blue' ? 'text-blue-600' : ''}
                    ${phase.color === 'purple' ? 'text-purple-600' : ''}
                    ${phase.color === 'orange' ? 'text-orange-600' : ''}
                    ${phase.color === 'green' ? 'text-green-600' : ''}
                    ${phase.color === 'red' ? 'text-red-600' : ''}
                  `}>
                    {phase.content.title}
                  </h3>
                  
                  <div className="space-y-3">
                    {phase.content.sections.map((section, idx) => (
                      <div key={idx}>
                        <h4 className={`
                          font-semibold text-sm mb-1
                          ${phase.color === 'blue' ? 'text-blue-500' : ''}
                          ${phase.color === 'purple' ? 'text-purple-500' : ''}
                          ${phase.color === 'orange' ? 'text-orange-500' : ''}
                          ${phase.color === 'green' ? 'text-green-500' : ''}
                          ${phase.color === 'red' ? 'text-red-500' : ''}
                        `}>
                          {section.title}
                        </h4>
                        <ul className="space-y-0.5">
                          {section.items.map((item, i) => (
                            <li key={i} className="text-xs text-gray-700 flex gap-1">
                              <span className={`mt-0.5
                                ${phase.color === 'blue' ? 'text-blue-400' : ''}
                                ${phase.color === 'purple' ? 'text-purple-400' : ''}
                                ${phase.color === 'orange' ? 'text-orange-400' : ''}
                                ${phase.color === 'green' ? 'text-green-400' : ''}
                                ${phase.color === 'red' ? 'text-red-400' : ''}
                              `}>•</span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                  
                  {phase.content.tip && (
                    <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-gray-700">
                      {phase.content.tip}
                    </div>
                  )}
                  
                  {phase.content.success && (
                    <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded text-xs text-gray-700">
                      {phase.content.success}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      )}

      {/* Mentorship Message with Detailed Content */}
      <div className="mt-8 bg-emerald-900/30 border border-emerald-600/50 rounded-lg p-6">
        <div className="flex items-center justify-center gap-2 mb-4">
          <span className="text-2xl">🤝</span>
          <h4 className="text-xl font-bold text-emerald-400">The Ladder is Mentorship</h4>
          <span className="text-2xl">🪜</span>
        </div>
        <p className="text-gray-300 text-sm max-w-2xl mx-auto mb-6">
          Throughout your climb, mentorship provides the stable support you need. 
          Find experienced hunters who can guide you safely up each rung, 
          sharing wisdom that makes your journey both safer and more successful.
        </p>
        
        <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto text-left">
          <div>
            <h5 className="text-emerald-400 font-semibold mb-3">Finding a Mentor</h5>
            <ul className="space-y-2 text-sm">
              <li className="flex gap-2 text-gray-300">
                <span className="text-emerald-400">•</span>
                <span>State 'Learn to Hunt' programs (free)</span>
              </li>
              <li className="flex gap-2 text-gray-300">
                <span className="text-emerald-400">•</span>
                <span>Local hunting clubs</span>
              </li>
              <li className="flex gap-2 text-gray-300">
                <span className="text-emerald-400">•</span>
                <span>Sporting goods store boards</span>
              </li>
              <li className="flex gap-2 text-gray-300">
                <span className="text-emerald-400">•</span>
                <span>Conservation groups (NWTF, QDMA, DU)</span>
              </li>
              <li className="flex gap-2 text-gray-300">
                <span className="text-emerald-400">•</span>
                <span>Church/community hunting groups</span>
              </li>
            </ul>
          </div>
          
          <div>
            <h5 className="text-emerald-400 font-semibold mb-3">Essential Resources</h5>
            <ul className="space-y-2 text-sm">
              <li className="flex gap-2 text-gray-300">
                <span className="text-emerald-400">•</span>
                <span>State wildlife agency website</span>
              </li>
              <li className="flex gap-2 text-gray-300">
                <span className="text-emerald-400">•</span>
                <span>onX Hunt or HuntStand apps</span>
              </li>
              <li className="flex gap-2 text-gray-300">
                <span className="text-emerald-400">•</span>
                <span>MeatEater podcast/website</span>
              </li>
              <li className="flex gap-2 text-gray-300">
                <span className="text-emerald-400">•</span>
                <span>Local processor list</span>
              </li>
              <li className="flex gap-2 text-gray-300">
                <span className="text-emerald-400">•</span>
                <span>Weather apps with wind data</span>
              </li>
              <li className="flex gap-2 text-gray-300">
                <span className="text-emerald-400">•</span>
                <span>First aid/CPR certification</span>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="mt-6 p-3 bg-emerald-500/20 border border-emerald-500/30 rounded-lg">
          <p className="text-center text-emerald-300 font-semibold">
            📊 70% success rate with mentorship vs 30% without
          </p>
        </div>
      </div>

      {/* Instructions */}
      <div className="mt-6 text-center">
        <p className="text-gray-400 text-sm">
          💡 Click on any image to learn more about that phase of your journey
        </p>
      </div>
    </div>
  );
}